import { FieldArray, Formik } from 'formik';
import { Alert, Button, Card, CardBody, CardFooter, Checkbox, Heading, IconChevron, IconInfo } from '../../ui';
import * as yup from 'yup';
import { careTeamOptionsList } from './types';
import { RecipientType, recipientTypeValues } from '../../api/consent/types';
import { useReferOrgInfo } from './useReferOrgInfo';

const validationSchema = yup.object({
    careTeamChoice: yup
        .array(
            yup.object({
                recipientType: yup
                    .string()
                    .oneOf([...recipientTypeValues])
                    .required('Required'),
                selected: yup.bool().required('Required'),
            })
        )
        .length(recipientTypeValues.length),
});

export interface CareTeamChoiceFormValues {
    careTeamList: RecipientType[];
}

interface CareTeamChoiceProps {
    careTeamList: RecipientType[];
    childName: string;
    onSubmit: (values: CareTeamChoiceFormValues) => Promise<void> | void;
    onBack: (values: CareTeamChoiceFormValues) => void;
}

export function CareTeamChoice({ careTeamList, childName, onSubmit, onBack }: CareTeamChoiceProps) {
    const initialValues = { careTeamList };
    const { includeReferOrg, referOrgName } = useReferOrgInfo();
    const referringOrgItem = careTeamOptionsList.find((item) => item.recipientType === 'referringOrg');
    if (referringOrgItem) {
        referringOrgItem.shown = includeReferOrg;
        referringOrgItem.label = referOrgName;
    }

    return (
        <div className="max-w-[684px] mx-auto my-20 space-y-6">
            <div className="text-center space-y-6">
                <Heading level={1}>Create {childName}'s care team</Heading>
                <p>
                    We recommend forming a care team, which is clinically proven to benefit progress, as our therapist
                    can notify other invested parties to support {childName}’s growth.
                </p>
            </div>

            <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
                {({ isSubmitting, handleSubmit, setFieldValue, values }) => (
                    <form onSubmit={handleSubmit} noValidate>
                        <Card>
                            <CardBody>
                                <div className="space-y-6">
                                    <Alert type="info">
                                        <div className="flex items-start space-x-2 gap-2">
                                            <div>
                                                <IconInfo />
                                            </div>
                                            {childName}’s therapist will only share information when they feel it’s
                                            relevant, with who it’s relevant to. You’ll be notified when this happens.
                                        </div>
                                    </Alert>
                                    <p>
                                        I give my permission for letters regarding {childName} and family's needs to be
                                        sent to the following recipients:
                                    </p>
                                    <FieldArray name="careTeamList">
                                        {() =>
                                            careTeamOptionsList
                                                .filter(({ shown }) => shown)
                                                .map(({ label, recipientType }, index) => (
                                                    <Checkbox
                                                        name={`careTeamList.${index}.selected`}
                                                        value={recipientType}
                                                        checked={values.careTeamList.indexOf(recipientType) > -1}
                                                        onChange={(e) => {
                                                            const newArray = values.careTeamList.slice();
                                                            const val = e.currentTarget.value as RecipientType;
                                                            const index = newArray.indexOf(val);
                                                            if (index > -1) {
                                                                newArray.splice(index, 1);
                                                            } else {
                                                                newArray.push(val);
                                                            }
                                                            setFieldValue('careTeamList', newArray);
                                                        }}
                                                        hideError
                                                        key={recipientType}
                                                    >
                                                        {label}
                                                    </Checkbox>
                                                ))
                                        }
                                    </FieldArray>
                                </div>
                            </CardBody>
                            <CardFooter>
                                <div className="flex justify-between items-center">
                                    <Button onClick={() => onBack(values)} variant="tertiary">
                                        <div className="flex items-center gap-2 text-green-500">
                                            <IconChevron />
                                            <span>Back</span>
                                        </div>
                                    </Button>
                                    <Button isProcessing={isSubmitting} type="submit" disabled={isSubmitting}>
                                        Continue
                                    </Button>
                                </div>
                            </CardFooter>
                        </Card>
                    </form>
                )}
            </Formik>
        </div>
    );
}
